import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';

type SessionConfig = {
  submissionId?: number;
  materialId?: number;
  docServerUrl: string;
  documentType: string;
  document: { url: string; key: string; fileType: string; title: string; permissions?: Record<string, unknown> };
  editorConfig: { callbackUrl: string; mode?: string; lang?: string; customization?: Record<string, unknown>; user?: { id: string; name: string } };
  token?: string | null;
};

type Props = {
  isOpen: boolean;
  assignmentId: number | null;
  session: SessionConfig | null;
  title?: string;
  showFinalize?: boolean;
  hintText?: string;
  isFinalizing?: boolean;
  onClose: () => void;
  onFinalize?: () => void;
};

export default function OnlyOfficeSessionModal({
  isOpen,
  assignmentId,
  session,
  title = 'OnlyOffice Bearbeitung',
  showFinalize = true,
  hintText = 'Nach dem Speichern im Editor hier final einreichen.',
  isFinalizing = false,
  onClose,
  onFinalize
}: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const editorDivId = assignmentId ? `classwork-onlyoffice-editor-${assignmentId}` : 'classwork-onlyoffice-editor';

  useEffect(() => {
    if (!isOpen || !session) return;
    setLoading(true);
    setError(null);
    const docServerUrl = String(session.docServerUrl || '').replace(/\/$/, '');
    if (!docServerUrl) {
      setError('OnlyOffice-Server ist nicht verfügbar');
      setLoading(false);
      return;
    }
    const scriptUrl = `${docServerUrl}/web-apps/apps/api/documents/api.js`;
    const initEditor = () => {
      const DocsAPI = (window as unknown as { DocsAPI?: { DocEditor: new (id: string, config: object) => void } }).DocsAPI;
      if (!DocsAPI?.DocEditor) {
        setError('OnlyOffice API konnte nicht geladen werden');
        setLoading(false);
        return;
      }
      try {
        const cfg: Record<string, unknown> = {
          documentType: session.documentType,
          document: session.document,
          editorConfig: session.editorConfig
        };
        if (session.token) cfg.token = session.token;
        new DocsAPI.DocEditor(editorDivId, cfg);
        setLoading(false);
      } catch {
        setError('Editor konnte nicht gestartet werden');
        setLoading(false);
      }
    };
    const existing = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existing) {
      initEditor();
    } else {
      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.onload = initEditor;
      script.onerror = () => {
        setError('OnlyOffice Script konnte nicht geladen werden');
        setLoading(false);
      };
      document.head.appendChild(script);
    }
    return () => {
      const docApi = (window as unknown as { DocsAPI?: { DocEditor?: (id: string) => { destroyEditor?: () => void } } }).DocsAPI;
      if (docApi?.DocEditor && typeof docApi.DocEditor === 'function') {
        try {
          const ed = docApi.DocEditor(editorDivId);
          if (ed?.destroyEditor) ed.destroyEditor();
        } catch (_) {}
      }
    };
  }, [isOpen, session, editorDivId]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl h-[86vh] flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <button type="button" className="btn-secondary !p-2" onClick={onClose} aria-label="Schließen">
            <X className="h-4 w-4" />
          </button>
        </div>
        {showFinalize ? (
          <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
            <p className="text-xs text-gray-600">{hintText}</p>
            <button type="button" className="btn-primary text-sm" onClick={onFinalize} disabled={isFinalizing || !session || !onFinalize}>
              {isFinalizing ? 'Reiche ein …' : 'Final einreichen'}
            </button>
          </div>
        ) : null}
        <div className="relative flex-1 bg-gray-50">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : null}
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-red-600">{error}</div>
          ) : null}
          <div id={editorDivId} className="w-full h-full" style={{ display: loading || error ? 'none' : 'block' }} />
        </div>
      </div>
    </div>
  );
}
